<script lang="ts">
	import {
		MacaroonVariant,
		macaroonVariantKeyMap,
		macaroonVariantNameMap,
	} from "../../../../core/schema/MacaroonVariant.js";
	import { inflectFit } from "./inflectFit.js";
	import { inflectMacaroons } from "./inflectMacaroons.js";
	import NumberInput from "../Form/NumberInput.svelte";
	import type { ProductMacaroons } from "../../../../core/schema/ProductMacaroons.js";
	import type { ProductSize } from "../../../../core/schema/ProductSize.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { maxMacaroonsPerSize, minMacaroons } from "../../../../core/schema/productOptionsConstants.js";
	import macaroon from "../../../../core/assets/icons/macaroon.svg";
	import plus from "../../../../core/assets/icons/btn-plus.svg?raw";
	import Button from "../Button.svelte";
	import { macaroonVariantColorMap } from "../../../../core/schema/macaroonVariantColorMap.js";
	import ProductOptionHeader from "./ProductOptionHeader.svelte";
	import { calculatePriceForProductOption } from "../../../../core/schema/utils/calculatePriceForProductOption.js";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";

	export let macaroons: ProductMacaroons | undefined;
	export let updateMacaroons: (macaroons: ProductMacaroons | undefined) => void;
	export let size: ProductSize;
	export let basePrice: number;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let changed: boolean | undefined;

	const { TENANT_LOCALE } = getGlobalSettings();

	const currentMacaroonsValues: ProductMacaroons =
		macaroons !== undefined
			? structuredClone(macaroons)
			: {
					[macaroonVariantKeyMap[MacaroonVariant.Chocolate]]: 0,
					[macaroonVariantKeyMap[MacaroonVariant.SaltedCaramel]]: 0,
					[macaroonVariantKeyMap[MacaroonVariant.Pistachio]]: 0,
					[macaroonVariantKeyMap[MacaroonVariant.Raspberry]]: 0,
					[macaroonVariantKeyMap[MacaroonVariant.Lemon]]: 0,
				};

	function onChange(_macaroons: typeof macaroons): void {
		if (changed === undefined) {
			changed = false;
			return;
		}
		changed = true;
	}
	$: onChange(currentMacaroonsValues);

	$: maxMacaroonsForSize = maxMacaroonsPerSize[size];
	$: currentAmount = Object.values(currentMacaroonsValues).reduce((acc, curr) => acc + curr, 0);
	$: totalLeftAmount = maxMacaroonsForSize - currentAmount;
	$: macaroons =
		currentAmount >= minMacaroons && currentAmount <= maxMacaroonsForSize ? currentMacaroonsValues : undefined;

	$: minimalPrice = minimalPricesForOptions[ProductOption.Macaroons];
	$: currentPrice = calculatePriceForProductOption(basePrice, ProductOption.Macaroons, macaroons);
</script>

<ProductOptionHeader name="Přidat barevné makronky" image={macaroon} price={currentPrice} {minimalPrice}>
	Přidej si na svůj dort až {maxMacaroonsForSize}
	{inflectMacaroons(maxMacaroonsForSize)} podle své chuti.
</ProductOptionHeader>

<div class="grid w-full grid-cols-2 gap-[.125rem] sm:gap-1.5">
	{#each Object.values(MacaroonVariant) as value}
		{@const key = macaroonVariantKeyMap[value]}
		{@const leftAmount = Math.max(0, maxMacaroonsForSize - currentAmount + (macaroons?.[key] ?? 0))}
		<div
			class="relative flex h-10 items-center justify-center rounded-md border-2 border-white text-[0.625rem] font-bold uppercase leading-4 text-secondary outline-2 outline-secondary {macaroonVariantColorMap[
				value
			]}"
		>
			{macaroonVariantNameMap[value][TENANT_LOCALE]}
		</div>
		<NumberInput
			id="macaroons-{value}-amount"
			min={0}
			max={leftAmount}
			required
			bind:valueAsNumber={currentMacaroonsValues[key]}
		/>
	{/each}
</div>

<div class="mt-4 flex flex-col">
	{#if totalLeftAmount > 0}
		<p>Na produkt se {inflectFit(totalLeftAmount)} ještě {totalLeftAmount} {inflectMacaroons(totalLeftAmount)}</p>
	{/if}
	<Button
		icon={plus}
		iconClass="w-5 h-5"
		class="mt-4 self-end"
		on:click={() => {
			updateMacaroons(macaroons);
		}}
	>
		Uložit úpravu
	</Button>
</div>
